import { useMemo, useState, type PropsWithChildren } from "react";
import { useGo, useNavigation, useTranslate, HttpError, useImport, useExport, useNotification } from "@refinedev/core";
import { CreateButton, ExportButton, useDataGrid, UseDataGridReturnType } from "@refinedev/mui";
import { IContact } from "@/utils/zod/contactFormValidation";
import { RefineListView } from "../../components";
import { ContactListTable } from "../../components/contact";
import { useLocation } from "react-router";
import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { ImportButton } from "@/components/override/ImportButton";
import { contactImportCSV } from "@/utils/data-provider/csvImportSchema";

export const ContactList = ({ children }: PropsWithChildren) => {
  const t = useTranslate();
  const { createUrl } = useNavigation();
  const go = useGo();
  const { pathname } = useLocation();
  const [importProgress, setImportProgress] = useState<{
    totalAmount: number;
    processedAmount: number;
  }>();
  const { open } = useNotification();
  const [tableTab, setTableTab] = useState<"pending" | "others">("others");

  const dataGrid: UseDataGridReturnType<IContact, HttpError, IContact> = useDataGrid<IContact>({
    resource: "accounts",
    pagination: {
      pageSize: 1000,
    },
  });

  const allContacts = dataGrid.tableQuery.data?.data ?? [];
  const pendingCount = useMemo(() => {
    return allContacts.filter(contact => contact.status === "pending").length;
  }, [allContacts]);

  const othersCount = useMemo(() => {
    return allContacts.filter(contact => contact.status !== "pending").length;
  }, [allContacts]);

  const tableQuery = {
    data: {
      data: tableTab === "pending" 
        ? allContacts.filter((contact: IContact) => contact.status === "pending") ?? []
        : allContacts.filter((contact: IContact) => contact.status !== "pending") ?? [],
      total: dataGrid.tableQuery.data?.total ?? 0
    },
    isLoading: dataGrid.tableQuery.isLoading,
    isError: dataGrid.tableQuery.isError
  };

  const { inputProps, isLoading: isImporting } = useImport({
    resource: "accounts",
    onFinish: (data) => {
      data.succeeded?.[0]?.response.forEach((response) => {
        if (response.error) {
          open?.({
            message: "Import Error",
            description: response.error,
            type: "error",
          });
        } else {
          open?.({
            message: "Import Success",
            description: response.message,
            type: "success",
          });
        }
      });
      data.errored?.[0]?.response.forEach((response) => {
        open?.({
          message: "Import Error",
          description: response.error,
          type: "error",
        });
      });
    },
    onProgress: (data) => {
      setImportProgress(data);
    },
    mapData: (data) => {
      const { user_phone_number, ...rest } = data;
      return {
        name: rest.name,
        status: rest.status.toLowerCase(),
        role_permission_name: rest.role_permission_name?.toLowerCase() || "user",
        notes: rest.notes,
        designation: rest.designation?.toLowerCase(),
        company_ledger_name: rest.company_ledger_name?.trim().toUpperCase(),
        user: {
          phone_number: user_phone_number ? Number(`91${user_phone_number}`) : null
        }
      }
    }
  })

  const { triggerExport, isLoading: isExporting } = useExport({
    resource: "accounts",
    download: true,
    mapData: (data) => {
      const mappedData = {
        name: data.name,
        status: data.status,
        role_permission_name: data.role_permission_name,
        notes: data.notes,
        designation: data.designation,
        company_ledger_name: data.company?.company_ledger_name,
        user_phone_number: data.user?.phone_number ? `91${data.user?.phone_number}` : "",
      }
      return mappedData
    }
  })

  return (
    <>
      <RefineListView
        breadcrumb={false}
        headerButtons={(props) => [
          <ToggleButtonGroup
            key="table-tab-toggle"
            value={tableTab}
            exclusive
            onChange={(_e, value) => setTableTab(value)}
          >
            <ToggleButton value="others" size="small">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>{t("accounts.fields.status.others")} ({othersCount})</Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="pending" size="small">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>{t("accounts.fields.status.pending")} ({pendingCount})</Typography>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>,
          <Typography variant="body2" color="text.secondary">
            {isImporting ? `Importing... ${importProgress?.processedAmount}/${importProgress?.totalAmount}` : isExporting ? "Exporting..." : undefined}
          </Typography>,
          <ExportButton onClick={triggerExport} disabled={isImporting || isExporting} />,
          <ImportButton
            inputProps={inputProps}
            csvHeaders={contactImportCSV}
            disabled={isImporting || isExporting}
            type="contact"
          />,
          <CreateButton
            {...props.createButtonProps}
            key="create"
            size="medium"
            sx={{ height: "40px" }}
            onClick={() => {
              return go({
                to: `${createUrl("accounts")}`,
                query: {
                  to: pathname,
                },
                options: {
                  keepQuery: true,
                },
                type: "replace",
              });
            }}
          >
            {t("accounts.titles.create")}
          </CreateButton>
        ]}
      >
        <ContactListTable tableQuery={tableQuery} />
      </RefineListView>
      {children}
    </>
  );
};
